import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const LearningJourney = () => {
  const [selectedYear, setSelectedYear] = useState(2025);

  const journeyData = [
    {
      year: 2025,
      title: "Advanced Architecture & Leadership",
      description: "Focusing on system design, team leadership, and emerging technologies",
      milestones: [
        { month: "Oct", title: "Full Stack Open", type: "certification", status: "completed", description: "University of Helsinki - 7 ECTS credits, Grade: 5/5", link: "https://studies.cs.helsinki.fi/stats/api/certificate/fullstackopen/en/05c08a72413e62f48fd8ced3d1346bc8" },
        { month: "Oct", title: "Full Stack Open - TypeScript", type: "certification", status: "completed", description: "University of Helsinki - 1 ECTS credit", link: "https://studies.cs.helsinki.fi/stats/courses/fs-typescript" },
        { month: "Oct", title: "Full Stack Open - GraphQL", type: "certification", status: "completed", description: "University of Helsinki - 1 ECTS credit", link: "https://studies.cs.helsinki.fi/stats/api/certificate/fs-graphql/en/a026a126830407d4d7d42ff2c2679776" },
        { month: "Oct", title: "Full Stack Open - Relational Databases (PostgreSQL)", type: "certification", status: "completed", description: "University of Helsinki - 1 ECTS credit", link: "https://studies.cs.helsinki.fi/stats/api/certificate/fs-psql/en/a1de6f8d2d0000713f83df27e8640dea" },
        { month: "Oct", title: "Full Stack Open - CI/CD", type: "certification", status: "completed", description: "University of Helsinki - 1 ECTS credit", link: "https://studies.cs.helsinki.fi/stats/api/certificate/fs-cicd/en/71fb76a703baeddb895fcb2075ea4570" },
        { month: "Oct", title: "Full Stack Open - React Native", type: "certification", status: "completed", description: "University of Helsinki - 2 ECTS credits", link: "https://studies.cs.helsinki.fi/stats/api/certificate/fs-react-native-2020/en/5a5ca336593b972f8db79c573c34de55" },
        { month: "Oct", title: "Ethics of AI", type: "certification", status: "completed", description: "University of Helsinki - 2 ECTS credits", link: "https://certificates.mooc.fi/validate/ai-ethics-cert" },
        { month: "Oct", title: "Full-Stack Portfolio Project", type: "achievement", status: "completed", description: "Built comprehensive portfolio with React, Supabase, and GitHub integration" },
        { month: "Sep", title: "Java Programming II", type: "certification", status: "completed", description: "University of Helsinki - 5 ECTS credits", link: "https://certificates.mooc.fi/validate/8bab5wi0yot" },
        { month: "Aug", title: "Java Programming I", type: "certification", status: "completed", description: "University of Helsinki - 5 ECTS credits", link: "https://certificates.mooc.fi/validate/qhpg15d1gkl" },
        { month: "Jul", title: "Spring Boot Mastery", type: "skill", status: "completed", description: "Advanced Spring ecosystem and enterprise patterns" }
      ]
    },
    {
      year: 2024,
      title: "Full-Stack Mastery & DevOps",
      description: "Deepening expertise in modern web technologies and infrastructure",
      milestones: [
        { month: "Dec", title: "Next.js & React Advanced", type: "skill", status: "completed", description: "Server components and modern React patterns" },
        { month: "Oct", title: "Docker & CI/CD Specialist", type: "certification", status: "completed", description: "Containerization and GitHub Actions automation" },
        { month: "Aug", title: "TypeScript Advanced Patterns", type: "course", status: "completed", description: "Generic types, decorators, and advanced patterns" },
        { month: "Jun", title: "Senior Developer Role", type: "achievement", status: "completed", description: "Advanced full-stack development responsibilities" }
      ]
    },
    {
      year: 2023,
      title: "Modern Frontend & Backend",
      description: "Building expertise in React ecosystem and enterprise development",
      milestones: [
        { month: "Nov", title: "React 18 & Concurrent Features", type: "skill", status: "completed", description: "Suspense, concurrent rendering, and new hooks" },
        { month: "Sep", title: "Java Enterprise Development", type: "certification", status: "completed", description: "Java SE/EE and Spring Framework certification" },
        { month: "Jul", title: "Database Design & Optimization", type: "course", status: "completed", description: "PostgreSQL and MySQL performance tuning" },
        { month: "May", title: "First Full-Stack Project", type: "achievement", status: "completed", description: "Built and deployed complete enterprise application" }
      ]
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in-progress': return 'Clock';
      case 'planned': return 'Circle';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'in-progress': return 'text-warning';
      case 'planned': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'certification': return 'Award';
      case 'course': return 'BookOpen';
      case 'skill': return 'Code';
      case 'achievement': return 'Trophy';
      default: return 'Circle';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'certification': return 'bg-amber-500';
      case 'course': return 'bg-blue-500';
      case 'skill': return 'bg-emerald-500';
      case 'achievement': return 'bg-purple-500';
      default: return 'bg-slate-500';
    }
  };

  const selectedYearData = journeyData?.find(year => year?.year === selectedYear);

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-foreground">Learning Journey</h3>
          <p className="text-muted-foreground">Professional development timeline</p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
          <span className="text-sm text-muted-foreground">3 years of growth</span>
        </div>
      </div>
      <div className="grid lg:grid-cols-4 gap-6">
        <div className="space-y-2">
          <h4 className="font-semibold text-foreground mb-3">Timeline</h4>
          {journeyData?.map((yearData) => (
            <button key={yearData?.year} onClick={() => setSelectedYear(yearData?.year)} className={`w-full text-left p-3 rounded-lg border transition-all ${selectedYear === yearData?.year ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground'}`}>
              <div className="font-semibold">{yearData?.year}</div>
              <div className="text-sm opacity-80">{yearData?.milestones?.length} milestones</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3">
          {selectedYearData && (
            <div>
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-foreground">{selectedYearData?.title}</h4>
                <p className="text-muted-foreground">{selectedYearData?.description}</p>
              </div>

              <div className="space-y-4">
                {selectedYearData?.milestones?.map((milestone, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 ${getTypeColor(milestone?.type)} bg-opacity-10 rounded-lg flex items-center justify-center`}>
                        <Icon name={getTypeIcon(milestone?.type)} size={20} color={`var(--color-${milestone?.type === 'certification' ? 'warning' : milestone?.type === 'course' ? 'primary' : milestone?.type === 'skill' ? 'success' : 'secondary'})`} />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-foreground">{milestone?.title}</h5>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-muted-foreground font-mono">{milestone?.month} {selectedYear}</span>
                          <Icon name={getStatusIcon(milestone?.status)} size={16} className={getStatusColor(milestone?.status)} />
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">{milestone?.description}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(milestone?.type)} bg-opacity-20 text-foreground font-medium`}>{milestone?.type}</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${milestone?.status === 'completed' ? 'bg-success/20 text-success' : milestone?.status === 'in-progress' ? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'}`}>{milestone?.status?.replace('-', ' ')}</span>
                        {milestone?.link && (
                          <a href={milestone?.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            <Icon name="ExternalLink" size={12} className="mr-1" />
                            Verify
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
              <div className="text-2xl font-bold text-foreground">9</div>
            <div className="text-sm text-muted-foreground">Certifications</div>
          </div>
          <div className="text-center">
              <div className="text-2xl font-bold text-foreground">25</div>
            <div className="text-sm text-muted-foreground">ECTS Credits</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">24+</div>
            <div className="text-sm text-muted-foreground">Skills Acquired</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">3</div>
            <div className="text-sm text-muted-foreground">Years Learning</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningJourney;
